import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { t } from "../i18n";
import { useModalKeys } from "./Modal";

// A short form asked over the page, as creating a class is (pages/Classes'
// NewClassDialog): a few fields done where the eye already is, where a
// drawer suits what is browsed beside the page (the devices, a person's
// files). Its title and ✕, what is changed holds now (`now`), the fields, a
// refusal said inside it, Cancel and the action. Esc, ✕, Cancel or a press
// outside closes it — not the press outside where `keepOnOutside` says a
// stray click must not lose what is being set (an exam's review settings,
// byj-1wv.118.15); the focus goes to
// its first field when it opens and back to the button that opened it — the
// one naming it in aria-controls — when it closes (byj-1wv.51.13). Its
// action is awaited, the button off meanwhile; one refused is said in it,
// which stays open, until a field is changed or it is tried again — every
// dialog alike, so none keeps its own busy and refusal.

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

export function FormDialog({ id, title, now, submitLabel, canSubmit, onSubmit, onClose, testid, className, keepOnOutside = false, children }: {
  /** the element id, which the opening button names in aria-controls */
  id: string;
  title: ReactNode;
  /** what the one or several changed hold now, said under the title */
  now?: ReactNode;
  submitLabel: string;
  canSubmit: boolean;
  /** the action; what it throws is said in the dialog, which stays open */
  onSubmit: () => Promise<void>;
  onClose: () => void;
  testid?: string;
  /** the dialog's own class beside form-modal: one that needs more room (a blueprint's editor) */
  className?: string;
  /** a press outside leaves it open: closed only by Esc, ✕ or Cancel */
  keepOnOutside?: boolean;
  children: ReactNode;
}) {
  const form = useRef<HTMLFormElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keys = useModalKeys(onClose);
  useEffect(() => {
    // as Drawer finds it: the button naming it, else what had the focus — a
    // row's ⋯, which its menu focuses before an item is done
    const opener =
      document.querySelector<HTMLElement>(`[aria-controls="${id}"]`) ??
      (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    form.current?.querySelector<HTMLElement>("input, select, textarea")?.focus();
    return () => opener?.focus();
  }, [id]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop" onClick={(e) => !keepOnOutside && e.target === e.currentTarget && onClose()}>
      <form
        ref={form}
        id={id}
        className={`modal form-modal stack${className ? ` ${className}` : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        data-testid={testid}
        onKeyDown={keys}
        // a field changed, the refusal said is no longer about it
        onChange={() => setError(null)}
        onSubmit={(e) => void submit(e)}
      >
        <div className="row">
          <h3 id={`${id}-title`} className="grow flush">
            {title}
          </h3>
          <button type="button" className="small" aria-label={t("common:close")} onClick={onClose}>
            ✕
          </button>
        </div>
        {now && (
          <p className="muted small flush" data-testid="now">
            {now}
          </p>
        )}
        {children}
        {error && (
          <p className="error flush" role="alert">
            {t("common:error", { m: error })}
          </p>
        )}
        <div className="row modal-actions">
          <button type="button" onClick={onClose}>
            {t("common:cancel")}
          </button>
          <button type="submit" className="primary" disabled={busy || !canSubmit}>
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
